import { useCallback, useRef } from 'react'
import { IconButton, Stack } from '@mui/material'
import {
  FacebookAuthProvider,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  signInWithRedirect
} from 'firebase/auth'
import { prefersAuthRedirect } from 'src/utils/authBrowser'
import { authWebLog } from 'src/utils/firebaseWebErrorReport'
import { useLocation, useNavigate } from 'react-router-dom'
import { FaApple, FaFacebook } from 'react-icons/fa'
import { FcGoogle } from 'react-icons/fc'
import { auth } from 'src/shims/firebase'

type Props = { setError: (msg: string) => void }

type LoginLocationState = { from?: { pathname: string } }

const circleSx = {
  width: 48,
  height: 48,
  border: '1px solid #e2e8f0',
  borderRadius: '50%',
  transition: 'border-color 0.15s ease',
  '&:hover': { borderColor: '#94a3b8', backgroundColor: '#f8fafc' }
}

export function SocialAuth({ setError }: Props) {
  const navigate = useNavigate()
  const location = useLocation()

  const goAfterSignIn = useCallback(() => {
    const from = (location.state as LoginLocationState | null)?.from?.pathname
    navigate(from && from !== '/login' ? from : '/dashboard', { replace: true })
  }, [location.state, navigate])

  const handleError = useCallback(
    (err: any) => {
      const code = err?.code ?? ''
      if (code === 'auth/account-exists-with-different-credential')
        setError('Un compte existe déjà avec un autre fournisseur.')
      else if (code === 'auth/popup-closed-by-user') return
      else setError(err?.message ?? 'Erreur de connexion.')
    },
    [setError]
  )

  const signInOAuth = useCallback(
    async (runPopup: () => Promise<unknown>, runRedirect: () => Promise<unknown>) => {
      if (prefersAuthRedirect()) {
        await runRedirect()
        return
      }
      await runPopup()
      goAfterSignIn()
    },
    [goAfterSignIn]
  )

  /** Même instance pour popup puis redirect fallback (évite incohérence OAuth). */
  const googleProviderRef = useRef<GoogleAuthProvider | null>(null)

  const onGoogle = useCallback(async () => {
    const provider = googleProviderRef.current ?? new GoogleAuthProvider()
    googleProviderRef.current = provider
    try {
      authWebLog('google_click', { strategy: 'popup_then_redirect_if_blocked' })
      try {
        const cred = await signInWithPopup(auth, provider)
        authWebLog('google_popup_success', {
          uid: cred.user.uid,
          emailVerified: cred.user.emailVerified,
          isAnonymous: cred.user.isAnonymous,
          providerIds: cred.user.providerData.map((p) => p.providerId).join(','),
        })
        goAfterSignIn()
      } catch (popupErr: unknown) {
        const pe = popupErr as { code?: string }
        const pc = pe?.code ?? ''
        if (pc === 'auth/popup-blocked' || pc === 'auth/cancelled-popup-request') {
          authWebLog('google_fallback_redirect', { afterPopupCode: pc })
          await signInWithRedirect(auth, provider)
          return
        }
        if (pc === 'auth/popup-closed-by-user') return
        throw popupErr
      }
    } catch (err: unknown) {
      const e = err as { code?: string; message?: string }
      authWebLog('google_error', {
        code: e?.code ?? 'unknown',
        message: (e?.message ?? String(err)).slice(0, 240),
      })
      handleError(err)
    }
  }, [goAfterSignIn, handleError])

  const onFacebook = useCallback(async () => {
    try {
      const provider = new FacebookAuthProvider()
      await signInOAuth(
        () => signInWithPopup(auth, provider),
        () => signInWithRedirect(auth, provider)
      )
    } catch (err) {
      handleError(err)
    }
  }, [handleError, signInOAuth])

  const onApple = useCallback(async () => {
    const provider = new OAuthProvider('apple.com')
    provider.addScope('email')
    provider.addScope('name')
    provider.setCustomParameters({ locale: 'fr' })
    try {
      // Apple sur le web : toujours redirect (popup souvent bloqué ou refusé par Safari / WebKit).
      await signInWithRedirect(auth, provider)
    } catch (err) {
      handleError(err)
    }
  }, [handleError])

  return (
    <Stack direction="row" spacing={2} justifyContent="center">
      <IconButton sx={circleSx} onClick={onApple}>
        <FaApple size={22} color="#000" />
      </IconButton>
      <IconButton sx={circleSx} onClick={onFacebook}>
        <FaFacebook size={20} color="#1877F2" />
      </IconButton>
      <IconButton sx={circleSx} onClick={onGoogle}>
        <FcGoogle size={20} />
      </IconButton>
    </Stack>
  )
}
